import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  ContentControlledEditingDirectiveViewModel,
  SegmentExtender,
  SegmentExtenderKeyframe,
} from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';

import { ContentControlStudioVODViewModel } from '../content-control-studio-vod-view-model';
import {
  ContentControlStudioBaseTimelineComponent,
  TimeBlock,
  TimeBlockTick,
  TooltipPosition,
} from './content-control-studio-base-timeline.component';
import { VideoTimeFrameMetadata } from './content-control-studio-video-metadata';
import { ContentControlStudioBaseViewModel } from '../content-control-studio-base-view-model';

export { KeyframeClickPayload, TimeBlockTick, TimeBlock } from './content-control-studio-base-timeline.component';

/**
 * VOD (Video-On-Demand) variant of the studio timeline.
 *
 * Renders a horizontal ruler with time blocks, positions the playhead based on
 * the video element's current time, and manages segment extenders for editing
 * directives. Relies on the native `seeked` event and a `requestAnimationFrame`
 * loop to keep the playhead synchronised with video playback.
 */
@Component({
  selector: 'evrideo-content-control-studio-vod-timeline',
  templateUrl: './content-control-studio-timeline.component.html',
  styleUrl: './content-control-studio-timeline.component.scss',
})
export class ContentControlStudioVODTimelineComponent
  extends ContentControlStudioBaseTimelineComponent
  implements OnDestroy
{
  // -------------------------------------------------------------------------
  // @Input properties (VOD-specific)
  // -------------------------------------------------------------------------

  /** The studio view model that drives this timeline's state and playback. */
  @Input('studio-view-model') vm!: ContentControlStudioBaseViewModel;

  // -------------------------------------------------------------------------
  // Private fields
  // -------------------------------------------------------------------------

  /** Bound reference to the `seeked` event handler, stored for cleanup in {@link ngOnDestroy}. */
  private _boundSeekedHandler: (() => void) | null = null;

  /** Bound reference to the animation-loop callback, stored for cleanup in {@link ngOnDestroy}. */
  private _boundAnimationLoop: ((t: number) => void) | null = null;

  /** Active `requestAnimationFrame` ID, cancelled on destroy to stop the render loop. */
  private _animationFrameId: number | null = null;

  /** Returns the current editing directive segments from the view model. */
  protected get _editingDirectives(): Array<ContentControlledEditingDirectiveViewModel> {
    return this.vm?.segments ?? [];
  }

  // -------------------------------------------------------------------------
  // Constructor
  // -------------------------------------------------------------------------

  constructor(renderer: Renderer2, cd: ChangeDetectorRef) {
    super(renderer, cd);
  }

  // -------------------------------------------------------------------------
  // Lifecycle hooks
  // -------------------------------------------------------------------------

  /**
   * Removes the `seeked` event listener from the video source, cancels the
   * animation frame loop, and delegates to the base class for window-listener cleanup.
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    const videoEl = this.vm?.getVideoSource();
    if (videoEl && this._boundSeekedHandler) {
      videoEl.removeEventListener('seeked', this._boundSeekedHandler);
      this._boundSeekedHandler = null;
    }
    if (this._animationFrameId !== null) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
  }

  // -------------------------------------------------------------------------
  // Event handlers
  // -------------------------------------------------------------------------

  /**
   * Updates the tooltip with the frame-snapped time at the mouse position
   * and shows it above the cursor.
   */
  public override onMouseMove(event: MouseEvent): void {
    if (!this._currentEditingVideoTimeFrame) return;
    this._updateToolTipPosition(event.clientX, event.clientY);
    this._showTooltip = true;
  }

  /**
   * Initialises the VOD timeline once the video source reports its metadata.
   *
   * Registers a `seeked` listener for playhead updates, starts the animation
   * loop, sets the playhead height, and triggers the initial timeline layout.
   */
  public override onVideoMetadataLoaded(): void {
    const videoSource = this.vm.getVideoSource();
    if (!videoSource) {
      return;
    }
    this._boundSeekedHandler = () => this._updatePlayheadPosition();
    videoSource.addEventListener('seeked', this._boundSeekedHandler);
    this._boundAnimationLoop = (t: number) => this._animationLoop(t);
    this._animationFrameId = requestAnimationFrame(this._boundAnimationLoop);
    this.setPlayheadHeight();
    this._calculateVideoTimeline();
  }

  // -------------------------------------------------------------------------
  // Public methods
  // -------------------------------------------------------------------------

  /**
   * Returns the visible timeline duration in seconds.
   *
   * In fit-segment mode, returns the active segment span plus margins.
   * In ET mode, subtracts the total duration of cut segments from the video duration.
   * Otherwise, returns the full video duration.
   */
  public override currentVideoTimeSecs(isFitSegment: boolean, isEditedTimeline: boolean): number {
    if (isFitSegment) {
      if (!this._currentEditingVideoTimeFrame) {
        return 0;
      }
      return (
        this._currentEditingVideoTimeFrame.endsAtSecs -
        this._currentEditingVideoTimeFrame.startsAtSecs +
        this._currentEditingVideoTimeFrame.marginStartSecs +
        this._currentEditingVideoTimeFrame.marginEndSecs
      );
    }
    if (isEditedTimeline) {
      const cuts = this._editingDirectives.filter(
        (x) => x.action === ContentControlledEventActionEnum.Cut,
      );
      if (cuts && cuts.length > 0) {
        const cutsLength = cuts
          .map((x) => (x.endsAtMs - x.startsAtMs) / 1000)
          .reduce((previousSum, a) => previousSum + a, 0);
        return (
          this.vm.getVideoSource().durationSecs -
          (cutsLength + cuts.length * this.oneFrameSecs)
        );
      }
    }
    if (this.vm.getVideoSource()) {
      return this.vm.getVideoSource().durationSecs;
    }
    return 0;
  }

  /** Returns the current playback position in seconds from the video source. */
  public override currentVideoPlayTimeSecs(): number {
    if (this.vm.getVideoSource()) {
      return this.vm.getVideoSource().currentTimeSecs;
    }
    return 0;
  }

  /** Returns the current playback time in milliseconds for UI display. */
  public override currentVideoPlayTimeForUI(): number {
    return this.currentVideoPlayTimeInMs();
  }

  /** Returns the current pixel density (pixels per second) based on timeline width and duration. */
  public override pixelsPerSecond(): number {
    return this._timelineWidth() / this.currentVideoTimeSecs(this.isFitSegment, this._isET());
  }

  /**
   * Seeks the video source to the specified time.
   * @param time - Target time in seconds.
   */
  protected override _videoSeekToTime(time: number): void {
    const videoSource = this.vm.getVideoSource();
    if (!videoSource) {
      return;
    }
    videoSource.seekToVideoPlayerRelativeTimeInSecs(time);
  }

  /**
   * Finds a cut segment overlapping the given time.
   * Converts seconds to milliseconds and searches the editing directives.
   * @param timeToCheckSecs - Video-relative time in seconds.
   * @returns The matching cut segment, or `undefined` if none found.
   */
  public override getCutSegmentAtSpecificTime(
    timeToCheckSecs: number,
  ): ContentControlledEditingDirectiveViewModel | undefined {
    return this._editingDirectives.find(
      (x) =>
        x.action === ContentControlledEventActionEnum.Cut &&
        x.startsAtMs <= timeToCheckSecs * 1000 &&
        x.endsAtMs >= timeToCheckSecs * 1000,
    );
  }

  /**
   * Returns the seek time (seconds) to jump one frame past a cut segment's end.
   * Snaps the cut end to the closest frame boundary before advancing.
   * @param cutEndMs - The cut segment's end time in milliseconds.
   */
  public override getSeekTimeAfterCutEnd(cutEndMs: number): number {
    return this.vm.getVideoSource().currentPlayTimeAsClosestFrameSecs(cutEndMs / 1000) + this.oneFrameSecs;
  }

  /**
   * Seeks the video to the in or out point of the active segment.
   * VOD segments store times as video-relative milliseconds, so a simple
   * ms → secs conversion is sufficient. When no segment is active, seeking
   * to 'out' jumps to the end of the video. The player is paused before seeking.
   */
  public override goToSelectedEventTime(point: 'in' | 'out'): void {
    let time = 0;
    const active = this.getActiveSegment();
    const videoSource = this.vm.getVideoSource();
    if (active) {
      time =
        point === 'in'
          ? videoSource.currentPlayTimeAsClosestFrameSecs(active.item.startsAtMs / 1000)
          : videoSource.currentPlayTimeAsClosestFrameSecs(active.item.endsAtMs / 1000);
    } else if (point === 'out') {
      time = videoSource?.durationSecs ?? 0;
    }
    this.vm.pause();
    this._videoSeekToTime(time);
  }

  /**
   * Scrolls the timeline viewport to keep the playhead visible.
   *
   * In ET mode, adjusts for cut-segment gaps before computing the scroll offset.
   * @param type - `'afterZoom'` centres around the playhead, `'next'` keeps current position,
   *   `'previous'` scrolls back by one viewport width.
   */
  public override translateTimeline(type: 'afterZoom' | 'next' | 'previous' = 'afterZoom'): void {
    let scrollLeftPx = 0;

    if (this._isFitted() === false) {
      let timeSecs = this.currentVideoPlayTimeSecs();
      if (this._isET()) {
        timeSecs -= this._getCutEventsDurationInMsBeforeSpecificTime(timeSecs * 1000) / 1000;
      }
      scrollLeftPx = timeSecs * this.pixelsPerSecond();
      const timelineClientWidthPx = this.Timeline?.nativeElement?.clientWidth ?? 0;
      switch (type) {
        case 'afterZoom': {
          if (timelineClientWidthPx > 0 && scrollLeftPx > timelineClientWidthPx) {
            scrollLeftPx = scrollLeftPx - timelineClientWidthPx / 2;
            const pixelsLeft =
              (this.currentVideoTimeSecs(this.isFitSegment, this._isET()) - timeSecs) * this.pixelsPerSecond();
            if (pixelsLeft < timelineClientWidthPx) {
              scrollLeftPx -= timelineClientWidthPx / 2 - pixelsLeft;
            }
          } else {
            scrollLeftPx = 0;
          }
          break;
        }
        case 'next': {
          break;
        }
        case 'previous': {
          scrollLeftPx -= this.Timeline?.nativeElement?.clientWidth ?? 0;
          break;
        }
        default:
          break;
      }
    }
    const timelineVirtualScrollElement = this.TimelineVirtualScroll?.elementRef?.nativeElement;
    if (timelineVirtualScrollElement) {
      this._renderer.setProperty(timelineVirtualScrollElement, 'scrollLeft', scrollLeftPx);
    }
    this._translateTracks(scrollLeftPx);
  }

  /**
   * Rebuilds the visual segment extenders from editing directives.
   *
   * Filters directives by fit mode, converts each to a {@link SegmentExtender},
   * sorts by start time, and distributes into per-media-type arrays.
   * @param editingDirectives - Optional subset of directives; defaults to all view model segments.
   */
  public override rebuildSegmentsInternally(
    editingDirectives?: Array<ContentControlledEditingDirectiveViewModel>,
  ): void {
    const relevantDirectives = this._getRelevantDirectives(editingDirectives);
    if (!relevantDirectives) return;

    const internalSegments = relevantDirectives
      .map((vm) => this._buildSegmentExtender(vm, relevantDirectives))
      .toSorted(
        (firstSegment, secondSegment) =>
          firstSegment.item.startsAtMs - secondSegment.item.startsAtMs,
      );

    this._internalSegments = internalSegments;
    this._categorizeSegments(internalSegments);
  }

  // -------------------------------------------------------------------------
  // Protected methods
  // -------------------------------------------------------------------------

  /**
   * Recalculates the entire VOD timeline layout.
   *
   * Builds video metadata, recalculates block widths when fitted, generates
   * time blocks, and in ET mode pauses and seeks past any cut segment that
   * overlaps the current time. Finishes with a deferred UI refresh.
   */
  protected override _calculateVideoTimeline(): void {
    if (this.currentVideoTimeSecs(this.isFitSegment, this._isET()) === 0) return;

    const metadata = this._buildCurrentVideoPlayerMetadata();
    if (!metadata) return;
    this._currentEditingVideoTimeFrame = metadata;

    if (this._timelineBlockWidthPx === 0 || this._isFitted()) {
      this._calculateTimelineBlockWidth();
    }

    this._timeBlocks = this._buildTimeBlocks();

    this._scheduleUIRefresh();
  }

  /**
   * Seeks the playhead to the tooltip's current time on a timeline click.
   *
   * In ET mode (non-fit), adds accumulated cut durations to convert from
   * visual time to actual video time. Pauses the player before seeking.
   * During a drag, prevents default browser behaviour instead.
   * @param event - The mouse or touch event from the timeline click.
   */
  protected override _onTimelineClickedEventHandler(event?: MouseEvent | TouchEvent): void {
    if (
      this._isPlayheadDragging === false &&
      this._isTimelineDragging === false &&
      this._tooltipCurrentTime
    ) {
      let time = this._tooltipCurrentTime;
      if (this._isET() && this.isFitSegment === false) {
        time +=
          this._getAccumulatedCutEventsDurationInMsBeforeSpecificTime(time * 1000) / 1000;
      }
      this.vm.pause();
      this._videoSeekToTime(time);
    } else {
      event?.preventDefault();
      event?.stopPropagation();
    }
  }

  /** Delegates all VOD drag interactions to {@link _doDrags}. */
  protected override _onWindowMouseMove(e: MouseEvent): void {
    this._doDrags(e);
  }

  // -------------------------------------------------------------------------
  // Private methods
  // -------------------------------------------------------------------------

  /**
   * Returns the current video play time in milliseconds, adjusted for the playhead.
   * In ET mode (non-fit), subtracts the duration of cut segments before the
   * current time so the playhead skips over collapsed gaps.
   */
  private _currentVideoPlayTimeForPlayheadMs(): number {
    if (this.vm.getVideoSource()) {
      let adjustmentsSecs = 0;
      if (this._isET() && this.isFitSegment === false) {
        adjustmentsSecs =
          this._getCutEventsDurationInMsBeforeSpecificTime(
            (this.vm.getVideoSource().currentTimeSecs - this.oneFrameSecs) * 1000,
          ) / 1000;
      }
      return (this.vm.getVideoSource().currentTimeSecs - adjustmentsSecs) * 1000;
    }
    return 0;
  }

  /** Returns the current horizontal scroll offset (px) of the virtual-scroll viewport. */
  private _virtualScrollLeft(): number {
    return this.TimelineVirtualScroll?.measureScrollOffset() ?? 0;
  }

  /**
   * Returns the effective pixel width of the timeline.
   * In fitted mode returns the visible container width; otherwise the total ruler width.
   */
  private _timelineWidth(): number {
    return this._isFitted() && this.Timeline?.nativeElement
      ? this.Timeline.nativeElement.offsetWidth
      : this.getTimelineBlocksLength();
  }

  /**
   * Positions the playhead DOM element based on the current video time.
   *
   * Converts the frame-snapped play time to pixels, adjusts for fit-segment
   * margins, and handles auto-scroll when the playhead reaches the viewport edge.
   * Skipped while the user is dragging the playhead.
   */
  protected _updatePlayheadPosition(): void {
    const videoEl = this.vm.getVideoSource();
    const timelineEl = this.Timeline?.nativeElement;
    const playheadEl = this.Playhead?.nativeElement;
    if (this._isPlayheadDragging === false && videoEl && timelineEl && playheadEl) {
      const pixels =
        this.vm.getVideoSource().currentPlayTimeAsClosestFrameSecs(this._currentVideoPlayTimeForPlayheadMs() / 1000) *
        this.pixelsPerSecond();
      if (pixels < 0) {
        return;
      }
      let pixelsLeft = pixels;
      if (this.isFitSegment && this._currentEditingVideoTimeFrame) {
        pixelsLeft -=
          (this._currentEditingVideoTimeFrame.startsAtSecs - this._currentEditingVideoTimeFrame.marginStartSecs) *
          this.pixelsPerSecond();
      }
      let timelineLeft =
        pixelsLeft - this._playheadHalfWidth - this._virtualScrollLeft();
      if (
        this._isFitted() === false &&
        videoEl.paused === false &&
        timelineLeft >= timelineEl.clientWidth + this._playheadHalfWidth
      ) {
        pixelsLeft = timelineLeft = 0;
        this.translateTimeline('next');
        this.rebuildSegmentsInternally();
      }
      this._playheadCurrentLeft = this._playheadPreviousLeft = pixelsLeft;
      this._renderer.setStyle(playheadEl, 'left', `${timelineLeft}px`);
    }
  }

  /**
   * Frame-rate-throttled animation loop.
   *
   * Updates the playhead position and checks fit-segment boundaries at most
   * once per video frame. Continues scheduling itself via
   * `requestAnimationFrame` while {@link _boundAnimationLoop} is set.
   * @param currentTime - High-resolution timestamp from `requestAnimationFrame`.
   */
  private _animationLoop(currentTime: number): void {
    if (
      currentTime - this._lastFrameTime >= this.oneFrameSecs ||
      this.vm.getVideoSource()?.paused
    ) {
      this._updatePlayheadPosition();
      this._pauseAtSegmentBoundary();
      this._lastFrameTime = currentTime;
    }
    if (this._boundAnimationLoop) {
      this._animationFrameId = requestAnimationFrame(this._boundAnimationLoop);
    }
  }

  /**
   * Computes and sets the tooltip time and position from mouse coordinates.
   *
   * Converts the mouse's pixel offset within the timeline to a frame-snapped
   * time in seconds, formats it, and positions the tooltip above the cursor.
   * @param eventClientX - The mouse X position relative to the viewport.
   * @param eventClientY - The mouse Y position relative to the viewport.
   */
  private _updateToolTipPosition(eventClientX: number, eventClientY: number): void {
    const rectLeft = this.TimelineVirtualScroll.measureBoundingClientRectWithScrollOffset('left');
    const mouseX = Math.max(
      0,
      eventClientX - rectLeft + this.TimelineVirtualScroll.measureScrollOffset(),
    );
    if (mouseX === 0) {
      this._tooltipCurrentTime = 0;
      this._tooltipFormattedCurrentTime = '00:00:00.000';
    } else {
      this._tooltipCurrentTime = this.vm.getVideoSource().currentPlayTimeAsClosestFrameSecs(
        mouseX / this.pixelsPerSecond() +
          (this._currentEditingVideoTimeFrame.startsAtSecs - this._currentEditingVideoTimeFrame.marginStartSecs),
      );
      this._tooltipFormattedCurrentTime = this._formatTimeSafe(
        this._tooltipCurrentTime * 1000,
      );
    }
    this._tooltipPosition = new TooltipPosition(eventClientX, eventClientY - 20);
  }

  /**
   * Builds video player metadata for the current timeline view.
   *
   * In fit-segment mode, scopes to the active segment with 1/6-duration margins
   * clamped to video boundaries. Otherwise, returns the full video duration.
   * @returns Metadata with start/end times and margins, or `null` if fit mode has no active segment.
   */
  protected _buildCurrentVideoPlayerMetadata(): VideoTimeFrameMetadata | null {
    if (this.isFitSegment) {
      const segment = this.getActiveSegment();
      if (!segment) return null;
      const meta = new VideoTimeFrameMetadata(
        segment.item.startsAtMs / 1000,
        segment.item.endsAtMs / 1000,
        0,
        0,
      );
      const margin = (meta.endsAtSecs - meta.startsAtSecs) / this._fitSegmentMarginDivisor;
      meta.marginStartSecs += margin <= meta.startsAtSecs ? margin : meta.startsAtSecs;
      const remainingToEnd = this.vm.getVideoSource()?.durationSecs - meta.endsAtSecs;
      meta.marginEndSecs +=
        this.vm.getVideoSource() && margin <= remainingToEnd
          ? margin
          : remainingToEnd ?? 0;
      return meta;
    }
    return new VideoTimeFrameMetadata(0, this.currentVideoTimeSecs(this.isFitSegment, this._isET()), 0, 0);
  }

  /**
   * Builds the ruler time blocks spanning the visible timeline duration.
   *
   * Creates evenly-sized blocks (last block may be shorter) with 10 tick marks
   * each. Each block's timestamp is frame-snapped via the video source.
   * @returns An array of time blocks covering the full timeline.
   */
  protected _buildTimeBlocks(): TimeBlock[] {
    const tickWidthPx = 1;
    const timeBlocks: TimeBlock[] = [];
    let remainingDurationSecs = this.currentVideoTimeSecs(this.isFitSegment, this._isET());
    let currentEditingVideoTimeSecs = this._currentEditingVideoTimeFrame.startsAtSecs - this._currentEditingVideoTimeFrame.marginStartSecs;

    while (remainingDurationSecs > 0) {
      const timeTicks: TimeBlockTick[] = [];
      const isLastBlock = remainingDurationSecs < this.timelineBlockDurationSecs;
      timeTicks.push(new TimeBlockTick(0, 35, 45, tickWidthPx));

      const actualBlockDurationSecs = isLastBlock
        ? remainingDurationSecs
        : this.timelineBlockDurationSecs;
      for (
        let i = 1;
        i < TimeBlock.TICKS_NUMBER && (i * actualBlockDurationSecs) / TimeBlock.TICKS_NUMBER < remainingDurationSecs;
        i++
      ) {
        timeTicks.push(
          new TimeBlockTick(i * (this._timelineBlockWidthPx / TimeBlock.TICKS_NUMBER), 40, 45, tickWidthPx),
        );
      }

      timeBlocks.push(
        new TimeBlock(
          timeTicks,
          isLastBlock
            ? this._timelineBlockWidthPx * (remainingDurationSecs / this.timelineBlockDurationSecs)
            : this._timelineBlockWidthPx,
          this.vm.getVideoSource().currentPlayTimeAsClosestFrameMs(currentEditingVideoTimeSecs),
        ),
      );

      remainingDurationSecs -= this.timelineBlockDurationSecs;
      currentEditingVideoTimeSecs += this.timelineBlockDurationSecs;
    }
    return timeBlocks;
  }

  /**
   * Schedules a deferred UI refresh after timeline recalculation.
   * Translates the timeline, updates the viewport size, resizes tracks,
   * and rebuilds segments on the next macrotask.
   */
  protected _scheduleUIRefresh(): void {
    setTimeout(() => {
      this.translateTimeline();
      this.TimelineVirtualScroll?.checkViewportSize();
      this._resizeTracks();
      this.rebuildSegmentsInternally();
    }, 0);
  }

  /** Filters relevant directives for segment rebuild, scoped to active segment when fit. */
  private _getRelevantDirectives(
    editingDirectives?: Array<ContentControlledEditingDirectiveViewModel>,
  ): Array<ContentControlledEditingDirectiveViewModel> | null {
    let relevantDirectives = editingDirectives
      ? [...editingDirectives]
      : [...this._editingDirectives];

    if (this.isFitSegment) {
      const activeSeg = this.getActiveSegment();
      if (!activeSeg) return null;
      relevantDirectives = relevantDirectives.filter((vm) => {
        return (
          (vm.startsAtMs <= activeSeg.item.startsAtMs &&
            vm.endsAtMs >= activeSeg.item.startsAtMs) ||
          (activeSeg.item.startsAtMs <= vm.startsAtMs &&
            activeSeg.item.endsAtMs >= vm.startsAtMs)
        );
      });
    }
    return relevantDirectives;
  }

  /** Converts a single editing directive into a SegmentExtender with keyframes. */
  private _buildSegmentExtender(
    vm: ContentControlledEditingDirectiveViewModel,
    relevantDirectives: Array<ContentControlledEditingDirectiveViewModel>,
  ): SegmentExtender {
    const segmentDurationMs = vm.endsAtMs - vm.startsAtMs;
    let segmentStartsAt = vm.startsAtMs;
    const activeSeg = this.getActiveSegment();

    if (this.isFitSegment && activeSeg?.item) {
      if (vm.id === activeSeg.item.id) {
        segmentStartsAt = this._currentEditingVideoTimeFrame.marginStartSecs * 1000;
      } else {
        const deltaStartsAtMs = activeSeg.item.startsAtMs - vm.startsAtMs;
        segmentStartsAt = this._currentEditingVideoTimeFrame.marginStartSecs * 1000 - deltaStartsAtMs;
      }
    }

    if (this._isET()) {
      const cutsBefore = relevantDirectives.filter(
        (cutVM) =>
          cutVM.action === ContentControlledEventActionEnum.Cut &&
          cutVM.startsAtMs < vm.startsAtMs,
      );
      if (cutsBefore && cutsBefore.length > 0) {
        segmentStartsAt -= cutsBefore
          .map((cutVM) => cutVM.durationMs)
          .reduce((firstNum, secondNum) => firstNum + secondNum);
      }
    }

    const segmentLeftPosition = (segmentStartsAt / 1000) * this.pixelsPerSecond();
    const segmentWidth = (segmentDurationMs / 1000) * this.pixelsPerSecond();
    const segmentKeyframes = this._buildSegmentKeyframes(vm, segmentWidth, segmentStartsAt);

    return new SegmentExtender(
      vm.id,
      vm,
      segmentWidth,
      100,
      segmentKeyframes,
      segmentLeftPosition,
      {},
    );
  }

  /** Builds the keyframe array for a single segment. */
  private _buildSegmentKeyframes(
    vm: ContentControlledEditingDirectiveViewModel,
    segmentWidth: number,
    segmentStartsAt: number,
  ): SegmentExtenderKeyframe[] {
    const keyframes: SegmentExtenderKeyframe[] = [];

    if (vm.keyframes && vm.keyframes.length === 0) {
      keyframes.push(new SegmentExtenderKeyframe(segmentWidth, false, segmentStartsAt));
      return keyframes;
    }

    let contentControlStartsAtMs = vm.startsAtMs;
    vm.keyframes
      .toSorted(
        (firstKeyframe, secondKeyframe) =>
          firstKeyframe.timeInMs - secondKeyframe.timeInMs,
      )
      .forEach((kf) => {
        const beforeKeyframe =
          ((kf.timeInMs - contentControlStartsAtMs) / 1000) * this.pixelsPerSecond();
        if (beforeKeyframe) {
          keyframes.push(
            new SegmentExtenderKeyframe(Math.abs(beforeKeyframe), false, kf.timeInMs),
          );
        }
        keyframes.push(
          new SegmentExtenderKeyframe(
            this.pixelsPerSecond() / this.videoFps,
            true,
            kf.timeInMs,
          ),
        );
        contentControlStartsAtMs = kf.timeInMs + this.oneFrameSecs * 1000;
      });
    if (vm.endsAtMs - contentControlStartsAtMs > 0) {
      keyframes.push(
        new SegmentExtenderKeyframe(
          Math.abs(
            ((vm.endsAtMs - contentControlStartsAtMs) / 1000) * this.pixelsPerSecond(),
          ),
          false,
          vm.endsAtMs,
        ),
      );
    }

    return keyframes;
  }

  /** Distributes built segments into categorized arrays by media type. */
  private _categorizeSegments(internalSegments: SegmentExtender[]): void {
    this._updateInclusiveSegments(internalSegments);
    this._audioSegments = this._filterSegments(ContentControlledMediaTypeEnum.Audio, internalSegments);
    this._setSimilarSegmentsStyle(this._audioSegments);
    this._eventNotificationSegments = this._filterSegments(ContentControlledMediaTypeEnum.External, internalSegments);
    this._setSimilarSegmentsStyle(this._eventNotificationSegments);
    this._videoSegments = this._filterSegments(ContentControlledMediaTypeEnum.Video, internalSegments);
    this._setSimilarSegmentsStyle(this._videoSegments);
    this._subtitlesSegments = this._filterSegments(ContentControlledMediaTypeEnum.Subtitles, internalSegments);
    this._setSimilarSegmentsStyle(this._subtitlesSegments);
    this._graphicsSegments = this._filterSegments(ContentControlledMediaTypeEnum.Graphics, internalSegments);
    this._setSimilarSegmentsStyle(this._graphicsSegments);
  }

  /**
   * Dispatches the mousemove event to the four independent VOD drag handlers
   * (timeline pan, playhead, mark-in, mark-out).
   */
  private _doDrags(e: MouseEvent): void {
    const currentPositionPx = e.clientX;
    this._doTimelineDrag(currentPositionPx);
    this._doPlayheadDrag(currentPositionPx);
    this._doInMarkDrag(currentPositionPx);
    this._doOutMarkDrag(currentPositionPx);
  }

  /**
   * Pans the virtual-scroll viewport horizontally during a timeline drag.
   * Clamps to prevent over-scrolling past the last two blocks.
   * @param currentPositionPx - Current mouse X position in viewport pixels.
   */
  private _doTimelineDrag(currentPositionPx: number): void {
    const scrollEl = this.TimelineVirtualScroll?.elementRef?.nativeElement;
    if (!this._isTimelineDragging || !scrollEl) return;

    const dx = currentPositionPx - this._timelineStartPosPx;
    if (scrollEl.scrollLeft < this._timelineWidth() - 2 * this._timelineBlockWidthPx) {
      this._renderer.setProperty(scrollEl, 'scrollLeft', scrollEl.scrollLeft - dx);
      this._translateTracks(this._virtualScrollLeft());
    } else {
      this._renderer.setProperty(
        scrollEl,
        'scrollLeft',
        this._timelineWidth() - 2 * this._timelineBlockWidthPx - this._playheadHalfWidth,
      );
    }
    this._timelineStartPosPx = currentPositionPx;
  }

  /**
   * Moves the playhead during a drag, converts its pixel position to a
   * frame-snapped time, adjusts for ET cut offsets, pauses the player,
   * and seeks to the new time.
   * @param currentPositionPx - Current mouse X position in viewport pixels.
   */
  private _doPlayheadDrag(currentPositionPx: number): void {
    const timelineEl = this.Timeline?.nativeElement;
    const playheadEl = this.Playhead?.nativeElement;
    if (!this._isPlayheadDragging || !timelineEl || !playheadEl) return;

    const dx = currentPositionPx - this._playheadStartPos;
    this._playheadCurrentLeft =
      this.isVideoTimelineFit === false && this.isFitSegment === false
        ? currentPositionPx - timelineEl.getBoundingClientRect().x
        : this._playheadPreviousLeft + dx;
    if (
      this._playheadCurrentLeft > this._timelineWidth() - this._playheadHalfWidth ||
      this._playheadCurrentLeft < 0
    ) {
      return;
    }
    this._renderer.setStyle(
      playheadEl,
      'left',
      `${this._playheadCurrentLeft - this._playheadHalfWidth}px`,
    );
    const baseTime =
      this.isFitSegment && this._currentEditingVideoTimeFrame
        ? this._currentEditingVideoTimeFrame.startsAtSecs - this._currentEditingVideoTimeFrame.marginStartSecs
        : 0;
    const seconds = this.vm.getVideoSource().currentPlayTimeAsClosestFrameSecs(
      this._pixelsToSecondsConvert(
        this._playheadCurrentLeft + this._virtualScrollLeft(),
      ) + baseTime,
    );
    let adjustments = 0;
    if (this._isET() && this.isFitSegment === false) {
      adjustments =
        this._getAccumulatedCutEventsDurationInMsBeforeSpecificTime(seconds * 1000) / 1000;
    }
    setTimeout(() => {
      this.vm.pause();
      this._videoSeekToTime(seconds + adjustments);
    }, 0);
  }

  /**
   * Adjusts the active segment's in-point (start time) during a mark-in drag.
   * Converts the new pixel position to time, validates bounds, and updates
   * the segment's `startsAtMs`, width, and left position.
   * @param currentPositionPx - Current mouse X position in viewport pixels.
   */
  private _doInMarkDrag(currentPositionPx: number): void {
    if (!this._isCurrentSegmentInMarkDragging || !this.selectedSegmentId) return;

    const selectedSegment = this.getActiveSegment();
    if (!selectedSegment) return;

    const dx = currentPositionPx - this._currentSegmentInMarkStartPos;
    const newLeft = selectedSegment.left + dx;
    const newStartsAtMs = this._pixelsToSecondsConvert(newLeft) * 1000;
    if (newStartsAtMs < 0 || newStartsAtMs >= selectedSegment.item.endsAtMs) return;

    selectedSegment.item.startsAtMs = newStartsAtMs;
    selectedSegment.width =
      ((selectedSegment.item.endsAtMs - newStartsAtMs) / 1000) * this.pixelsPerSecond();
    selectedSegment.left = newLeft;
    this._internalSegments = [...this._internalSegments];
  }

  /**
   * Adjusts the active segment's out-point (end time) during a mark-out drag.
   * Converts the new pixel position to time, validates bounds, and updates
   * the segment's `endsAtMs` and width.
   * @param currentPositionPx - Current mouse X position in viewport pixels.
   */
  private _doOutMarkDrag(currentPositionPx: number): void {
    if (!this._isCurrentSegmentOutMarkDragging || !this.selectedSegmentId) return;

    const selectedSegment = this.getActiveSegment();
    if (!selectedSegment) return;

    const dx =
      (currentPositionPx -
        this._currentSegmentOutMarkStartPos +
        selectedSegment.item.endsAtMs / 1000) *
      this.pixelsPerSecond();
    const currentPositionAsTime = this._pixelsToSecondsConvert(dx);
    const newEndsAtMs = currentPositionAsTime * 1000;
    if (
      newEndsAtMs > this.currentVideoTimeSecs(this.isFitSegment, this._isET()) ||
      newEndsAtMs <= selectedSegment.item.startsAtMs
    ) {
      return;
    }
    selectedSegment.item.endsAtMs = newEndsAtMs;
    selectedSegment.width =
      ((selectedSegment.item.endsAtMs - selectedSegment.item.startsAtMs) / 1000) *
      this.pixelsPerSecond();
    this._internalSegments = [...this._internalSegments];
  }

  /**
   * Auto-pauses the player when playback reaches the end of the fit-segment
   * boundary (segment end plus margin).
   */
  private _pauseAtSegmentBoundary(): void {
    if (
      this.isFitSegment &&
      this._currentEditingVideoTimeFrame &&
      this.currentVideoPlayTimeSecs() >=
        this._currentEditingVideoTimeFrame.endsAtSecs + this._currentEditingVideoTimeFrame.marginEndSecs
    ) {
      this.vm.pause();
    }
  }
}
